import { Location } from '@angular/common';
import { DestroyRef, EventEmitter, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { ListPagination } from '@workspace/data-types';
import { ApiService, ExtraQueryParam, QueryParamsUtils } from '@workspace/ng-http';
import { catchError, map, mergeMap, Observable, of, tap } from 'rxjs';

export abstract class BasicPaginationComponent<T> {
  pagination = signal<ListPagination>({
    page: 0,
    pageSize: 10,
  });

  extraParams: ExtraQueryParam[] = [];
  isLoading = signal<boolean>(true);
  items: T[] = [];
  count = signal<number>(0);
  specificCount = signal<number>(0);

  loading = new EventEmitter<boolean>();

  protected router = inject(Router);
  protected route = inject(ActivatedRoute);
  protected location = inject(Location);
  protected destroyRef = inject(DestroyRef);
  protected api = inject(ApiService);

  constructor() {
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      this.handlePaginationQueryParameters(params);
      this.handleExtraQueryParameters(params);
    });
  }

  protected handlePaginationQueryParameters(params: ParamMap): void {
    const search = params.get('search');
    const orderBy = params.get('orderBy');
    const order = params.get('order');
    const pageParam = params.get('page');
    const sizeParam = params.get('pageSize');

    if (pageParam !== null) {
      const page = Number.parseInt(pageParam);
      if (!Number.isNaN(page)) this.pagination().page = page;
    }

    if (sizeParam !== null) {
      const pageSize = Number.parseInt(sizeParam);
      if (!Number.isNaN(pageSize)) this.pagination().pageSize = pageSize;
    }

    if (search !== null) this.pagination().search = search;
    if (orderBy !== null) this.pagination().orderBy = orderBy;
    if (order !== null) this.pagination().order = order as 'asc' | 'desc';
  }

  protected handleExtraQueryParameters(params: ParamMap): void {
    const tab = params.get('tab');
    if (tab) {
      this.extraParams.push({ name: 'tab', value: tab });
    }
  }

  onPageChanged(event: PageEvent): void {
    this.pagination().page = event.pageIndex;
    this.pagination().pageSize = event.pageSize;
    this.doRefresh();
  }

  onSortChanged(newSort: Sort): void {
    this.pagination().order = newSort.direction === 'asc' ? 'asc' : 'desc';
    this.pagination().orderBy = newSort.active;
    this.doRefresh();
  }

  refresh(): Observable<T[]> {
    this.loading.emit(true);
    this.updateQueryParams();
    return this.getAll().pipe(
      takeUntilDestroyed(this.destroyRef),
      catchError((error) => {
        this.loading.emit(false);
        // TODO display error message to user (snackbar)
        throw error;
      }),
      map((items: T[]) => {
        this.items = items;
        this.loading.emit(false);
        return items;
      }),
      mergeMap((items: T[]) => {
        return this.countAll().pipe(
          map((count: number) => {
            this.count.set(count);
            return items;
          }),
        );
      }),
      mergeMap((items: T[]) => {
        return this.countSpecific().pipe(
          map((count: number) => {
            this.specificCount.set(count);
            return items;
          }),
        );
      }),
      tap(() => {
        this.afterItemsLoaded();
      }),
    );
  }

  protected updateQueryParams(): void {
    const queryParams = QueryParamsUtils.buildFilterParams(this.pagination(), false, this.extraParams);
    const currentPath = this.location.path(false).split('?')[0];
    this.location.replaceState(`${currentPath}${queryParams}`);
  }

  doRefresh(): void {
    this.refresh().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  private countSpecific(): Observable<number> {
    return of(0);
  }

  private afterItemsLoaded() {
    // hook to do stuff after items and count are loaded from API, can be overriden in child component
  }

  abstract getAll(): Observable<T[]>;
  abstract countAll(): Observable<number>;
}
